import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { CurrencyDollar, CheckCircle, ArrowUUpLeft, WarningOctagon, Clock } from "@phosphor-icons/react";

/**
 * v3.2.35 · Compact 5-row transactions strip. Drops into Settings or
 * any profile page. Role-aware: clients see payments made, PEs see
 * earnings received. Renders nothing when the user has no history —
 * avoids empty-state clutter for new accounts.
 */
export default function CompactTransactionsStrip({ role }) {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [busy, setBusy] = useState(true);
  const isPe = role === "pe";

  useEffect(() => {
    if (role !== "client" && role !== "pe") { setBusy(false); return; }
    (async () => {
      try {
        if (isPe) {
          const { data } = await api.get("/payments/earnings");
          setRows((data.rows || []).slice(0, 5));
          setSummary({ label: "Lifetime earned",
                         total: data.total_earnings_usd,
                         count: data.count });
        } else {
          const { data } = await api.get("/payments/history");
          const paid = (data.rows || []).filter((r) => r.payment_status === "paid");
          const totalPaid = paid.reduce((s, r) => s + Number(r.amount_usd || 0), 0);
          setRows((data.rows || []).slice(0, 5));
          setSummary({ label: "Lifetime paid",
                         total: totalPaid, count: paid.length });
        }
      } catch { /* silent — non-critical widget */ }
      finally { setBusy(false); }
    })();
  }, [role, isPe]);

  if (role !== "client" && role !== "pe") return null;
  if (busy) return null;
  if (rows.length === 0) return null;

  const chip = (r) => {
    if (r.dispute_status === "open") return { cls: "text-[#FF3B30]", icon: <WarningOctagon size={11} weight="fill" /> };
    if (r.payment_status === "refunded") return { cls: "text-gray-400", icon: <ArrowUUpLeft size={11} /> };
    if (r.payment_status === "paid") return { cls: "text-emerald-400", icon: <CheckCircle size={11} weight="fill" /> };
    return { cls: "text-[#FFCC00]", icon: <Clock size={11} /> };
  };

  return (
    <div className="mt-6 border border-white/10" data-testid="settings-transactions-strip">
      <div className="border-b border-white/10 p-4 bg-[#1A1F29] flex items-center justify-between">
        <div className="label-tag inline-flex items-center gap-2">
          <CurrencyDollar size={13} className="text-[#00E5FF]" weight="duotone" />
          {isPe ? "Recent earnings" : "Recent payments"}
        </div>
        <div className="text-xs font-mono text-gray-400 tabular-nums">
          <span className="text-white font-bold">${Number(summary?.total || 0).toLocaleString()}</span>
          <span className="text-gray-500"> USD · {summary?.count || 0} {isPe ? "settled" : "paid"}</span>
        </div>
      </div>
      <div className="divide-y divide-white/5">
        {rows.map((r) => {
          const c = chip(r);
          return (
            <Link key={r.session_id} to={`/jobs/${r.job_id}`}
              className="grid grid-cols-[1fr_100px_90px_60px] gap-2 items-center px-4 py-2.5 text-xs hover:bg-white/[0.02]"
              data-testid={`settings-tx-${r.session_id}`}>
              <div className="min-w-0">
                <div className="text-white truncate">MS{r.milestone_idx} · {r.milestone_title || <span className="italic text-gray-500">(untitled)</span>}</div>
                <div className="font-mono text-[10px] text-gray-500 truncate">{r.job_title}</div>
              </div>
              <div className="font-display font-black tabular-nums text-white">
                ${Number(isPe ? r.pe_earnings_usd : r.amount_usd || 0).toLocaleString()}
              </div>
              <div className="font-mono text-[10px] text-gray-500">
                {(r.paid_at || r.created_at) ? new Date(r.paid_at || r.created_at).toLocaleDateString() : "—"}
              </div>
              <div className={`inline-flex items-center gap-1 font-mono ${c.cls}`}>
                {c.icon}
              </div>
            </Link>
          );
        })}
      </div>
      <div className="border-t border-white/10 p-3 text-center">
        <Link to="/payments"
          className="text-xs font-mono text-[#00E5FF] hover:text-white uppercase tracking-wider"
          data-testid="settings-tx-view-all">
          view full history →
        </Link>
      </div>
    </div>
  );
}
